import { useState, useEffect } from 'react';
import { AnimatedNumber } from '../../components/AnimatedNumber/index';

const ANIMATION_DURATION = 750;

interface DashboardProps {
  user: any;
  onNavigate?: (page: string) => void;
}

interface DashboardStats {
  totalQuotes: number;
  activeWorkflows: number;
  totalValue: number;
  approvedQuotes: number;
}

export const Dashboard = ({ user, onNavigate }: DashboardProps) => {
  const [stats, setStats] = useState<DashboardStats>({
    totalQuotes: 0,
    activeWorkflows: 0,
    totalValue: 0,
    approvedQuotes: 0
  });
  const [loading, setLoading] = useState(true);
  const [recentQuotes, setRecentQuotes] = useState<any[]>([]);

  useEffect(() => {
    fetchDashboardData();
  }, []);

  const fetchDashboardData = async () => {
    try {
      // Fetch quotes to calculate stats
      const response = await fetch('/quotes', {
        headers: {
          'user-id': user.id
        }
      });
      
      const result = await response.json();
      if (result.success) {
        const quotes = result.data;
        
        // Calculate stats
        const totalQuotes = quotes.length;
        const activeWorkflows = quotes.filter((q: any) => q.workflowId && !q.approved).length;
        const totalValue = quotes.reduce((sum: number, q: any) => sum + (q.totalPrice || 0), 0);
        const approvedQuotes = quotes.filter((q: any) => q.approved).length;
        
        setStats({
          totalQuotes,
          activeWorkflows,
          totalValue,
          approvedQuotes
        });
        
        // Get recent quotes (last 5)
        const recent = quotes
          .sort((a: any, b: any) => new Date(b.createdAt).getTime() - new Date(a.createdAt).getTime())
          .slice(0, 5);
        setRecentQuotes(recent);
      }
    } catch (error) {
      console.error('Failed to fetch dashboard data:', error);
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="page">
      <h1>Welcome back, {user.name}!</h1>
      
      <div className="dashboard-grid mt-16">
        <div className="card stats-card">
          <h3>📊 Quick Stats</h3>
          {loading ? (
            <p>Loading stats...</p>
          ) : (
            <div className="stats-grid">
              <div className="stat">
                <div className="stat-number">
                  <AnimatedNumber value={stats.totalQuotes} duration={ANIMATION_DURATION} />
                </div>
                <div className="stat-label">Total Quotes</div>
              </div>
              <div className="stat">
                <div className="stat-number">
                  <AnimatedNumber value={stats.activeWorkflows} duration={ANIMATION_DURATION} />
                </div>
                <div className="stat-label">Active Workflows</div>
              </div>
              <div className="stat">
                <div className="stat-number">
                  <AnimatedNumber value={stats.totalValue} prefix="$" duration={ANIMATION_DURATION} />
                </div>
                <div className="stat-label">Total Value</div>
              </div>
              <div className="stat">
                <div className="stat-number">
                  <AnimatedNumber value={stats.approvedQuotes} duration={ANIMATION_DURATION} />
                </div>
                <div className="stat-label">Approved Quotes</div>
              </div>
            </div>
          )}
        </div>

        <div className="card actions-card">
          <h3>🚀 Quick Actions</h3>
          <div className="action-buttons">
            <button 
              onClick={() => onNavigate && onNavigate('create-quote')} 
              className="action-button pulsing-button"
            >
              📝 Create Quote
            </button>
            <button 
              onClick={() => onNavigate && onNavigate('quotes')} 
              className="action-button"
            >
              📋 View Quotes
            </button>
            <button 
              onClick={() => onNavigate && onNavigate('insights')} 
              className="action-button"
            >
              💡 View Insights
            </button>
          </div>
        </div>

        <div className="card activity-card">
          <h3>📈 Recent Activity</h3>
          {loading ? (
            <p>Loading recent activity...</p>
          ) : recentQuotes.length > 0 ? (
            <div className="recent-quotes">
              {recentQuotes.map((quote: any) => (
                <div key={quote._id} className="recent-quote-item">
                  <div className="recent-quote-info">
                    <div className="recent-quote-title">{quote.title}</div>
                    <div className="recent-quote-meta">
                      {quote.customer} • ${quote.totalPrice?.toLocaleString()} • {new Date(quote.createdAt).toLocaleDateString()}
                    </div>
                  </div>
                  <div className="recent-quote-status">
                    {quote.approved ? '✅' : '📋'}
                  </div>
                </div>
              ))}
            </div>
          ) : (
            <div className="activity-placeholder">
              <p>No recent activity</p>
              <p>Create your first quote to get started!</p>
            </div>
          )}
        </div>
      </div>
    </div>
  );
};